import { toast } from "react-toastify";
import axios from "axios";
import { AsYouType, isValidPhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";

export const validateEmail = (email) => {
  return email.match(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
};

export const validatePassword = (password) => {
  let response = { success: true, message: "" };
  if (password.length < 6) {
    response.success = false;
    response.message = "Password must be at least 6 letters";
  }

  return response;
};

// In dev the page is opened both from the desktop browser (localhost) and from
// the technician app's WebView over the LAN, where "localhost" would resolve to
// the phone itself. Follow whichever host served the page.
const devApiUrl = () => {
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:5000`;
};

export const API_URL =
  process.env.REACT_APP_ENV == "production"
    ? "https://makani.services"
    : process.env.REACT_APP_ENV == "staging"
      ? "https://test.makani.services"
      : process.env.REACT_APP_ENV == "devai"
        ? "https://devai.makani.services"
        : devApiUrl();

export const isAuthenticatedWithToken = () => {
  const token = localStorage.getItem("accessToken"); // or sessionStorage.getItem('jwtToken');

  if (token) {
    // try {
    //   const { exp } = jwt_decode(token);
    //   return exp > Date.now() / 1000;
    // } catch (error) {
    //   return false;
    // }
    return true;
  }
  return false;
};

export const handleError = (error) => {
  let message = "An error occured";

  try {
    if (error?.response) {
      const { data, status } = error.response;
      if (data?.message) {
        message = data.message;
      } else if (typeof data === "string" && data.trim().length) {
        message = data;
      } else if (data?.error) {
        message = data.error;
      } else if (status) {
        message = `Request failed (${status})`;
      }
    } else if (error?.message) {
      message = error.message;
    } else if (typeof error === "string" && error.trim().length) {
      message = error;
    }
  } catch (e) {
    //record this error so we can prevent the future error
    console.log("API error: ", e);
  }

  toast(message);
};

export const limitString = (str, maxLength) => {
  if (!str) return "";
  if (str.length > maxLength) {
    return str.slice(0, maxLength) + "...";
  } else {
    return str;
  }
};

export const sanitizeWoNumber = (fileName) => {
  if (!fileName) return "";
  // Replace path separators with a dash to keep the value readable but safe
  return fileName.replace(/[\\/]/g, "-");
};

export const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (event) {
      const base64String = event.target.result.split(",")[1]; // Extracting only the Base64 string
      console.log("Base64 Encoded File:", base64String);
      resolve(base64String);
    };

    reader.onerror = function (error) {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
};

export const isHeicFileName = (fileName = "") => {
  const name = String(fileName).toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif");
};

// Module-level cache keyed by the original HEIC URL so repeated requests reuse
// the already-converted JPEG object URL instead of re-fetching/re-decoding.
const heicObjectUrlCache = new Map();
const heicInflightRequests = new Map();

export const getCachedHeicObjectUrl = (url) => heicObjectUrlCache.get(url) || "";

export const convertHeicUrlToObjectUrl = async (url) => {
  if (!url) return "";

  const cached = heicObjectUrlCache.get(url);
  if (cached) return cached;

  const inflight = heicInflightRequests.get(url);
  if (inflight) return inflight;

  const request = (async () => {
    let response;
    try {
      response = await fetch(url, { mode: "cors" });
    } catch (fetchError) {
      // A network/CORS failure rejects fetch outright (no response object).
      throw new Error(`HEIC fetch failed (network/CORS) for ${url}: ${fetchError.message}`);
    }
    if (!response.ok) throw new Error(`HEIC fetch failed with HTTP ${response.status} for ${url}`);
    const blob = await response.blob();

    // heic2any bundles its libheif WebAssembly inline, so it works under CRA
    // without shipping/serving a separate .wasm file. It may return an array
    // (multi-image HEIC); we only need the primary image.
    const heic2any = (await import("heic2any")).default;
    let convertedBlob;
    try {
      const converted = await heic2any({ blob, toType: "image/jpeg", quality: 0.9 });
      convertedBlob = Array.isArray(converted) ? converted[0] : converted;
    } catch (decodeError) {
      // The file extension can lie: a JPEG/PNG mislabeled `.heic` is common on
      // iOS uploads. heic2any inspects the actual bytes and, for a file that is
      // already a browser-displayable image, throws ERR_USER "Image is already
      // browser readable". In that case the original blob renders fine as-is, so
      // fall back to it instead of failing.
      const message = decodeError?.message || decodeError?.code || String(decodeError);
      if (/already browser readable/i.test(message)) {
        convertedBlob = blob;
      } else {
        throw new Error(`HEIC decode failed for ${url} (blob type "${blob.type}", ${blob.size} bytes): ${message}`);
      }
    }
    const objectUrl = URL.createObjectURL(convertedBlob);
    heicObjectUrlCache.set(url, objectUrl);
    return objectUrl;
  })();

  heicInflightRequests.set(url, request);
  try {
    return await request;
  } finally {
    heicInflightRequests.delete(url);
  }
};

export const downloadImage = async (url, fileName) => {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error(`Request failed with ${response.status}`);

    const blob = await response.blob(); // Works for HEIC/other image types without canvas support
    const objectURL = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = objectURL;
    link.setAttribute("download", fileName || "download");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectURL);
  } catch (error) {
    console.error("Error downloading image:", error);
    toast("Unable to download image");
  }
};

export const downloadPdf = async (pdfURL, fileName) => {
  try {
    const response = await axios.get(pdfURL, {
      responseType: "blob",
    });

    const pdfBlob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(pdfBlob);
    const tempLink = document.createElement("a");
    tempLink.href = url;
    tempLink.setAttribute("download", fileName);
    document.body.appendChild(tempLink);
    tempLink.click();
    // Clean up the temporary elements and URL
    document.body.removeChild(tempLink);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading PDF:", error);
  }
};

export async function downloadFile(url, filename) {
  const response = await fetch(url);
  const blob = await response.blob();

  const urlBlob = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = urlBlob;
  a.download = filename || "download";

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(urlBlob); // Clean up
}

export const getUserNameArray = (userIdArray, userArray) => {
  if (userIdArray.length === 0) return "";
  let temp = [];
  for (let userId of userIdArray) {
    let user = userArray.find((item) => item.id == userId);
    if (user) {
      temp.push(user);
    }
  }
  const names = temp.map((user) => user.name);
  return names.join(", ");
};

export const getAssignedTechsNameArray = (assignedTechs) => {
  if (assignedTechs) {
    let primaryTech = assignedTechs.find((obj) => obj.roleId === 0);
    if (primaryTech) {
      let primaryTechIndex = assignedTechs.findIndex((obj) => obj.roleId === 0);
      assignedTechs.splice(primaryTechIndex, 1);
      assignedTechs.splice(0, 0, primaryTech);
    }
    let techNames = assignedTechs.map((obj) => obj.user.name);
    techNames = techNames.join(", ");
    return techNames;
  } else {
    return "Waiting for Assignment";
  }
};

/**
 * Permission check against the IAM policy model.
 *
 * Reads the effective allow/deny rule sets cached in localStorage (populated at
 * login from `/api/auth/me/permissions`, shape `{ allows, denies }`). Mirrors
 * the backend: explicit-deny-wins with wildcard awareness on both sides — a key
 * matches a set if it contains the exact key, `resource:*`, or `*:*`.
 *
 * Usage: `can("wo:delete")` — gate a button/route on a single permission.
 */
export const can = (permissionKey) => {
  let allows = [];
  let denies = [];
  try {
    const parsed = JSON.parse(localStorage.getItem("permissions"));
    if (Array.isArray(parsed)) {
      // Legacy flat format = allowed keys only.
      allows = parsed;
    } else {
      allows = Array.isArray(parsed?.allows) ? parsed.allows : [];
      denies = Array.isArray(parsed?.denies) ? parsed.denies : [];
    }
  } catch {
    allows = [];
    denies = [];
  }

  const resource = String(permissionKey).split(":")[0];
  const resourceWildcard = `${resource}:*`;
  const setMatches = (keys) =>
    Array.isArray(keys) && (keys.includes("*:*") || keys.includes(resourceWildcard) || keys.includes(permissionKey));

  if (setMatches(denies)) return false; // deny wins
  return setMatches(allows);
};

/** True if the user holds every key in the list. */
export const canAll = (permissionKeys = []) => permissionKeys.every((k) => can(k));

/** True if the user holds any key in the list. */
export const canAny = (permissionKeys = []) => permissionKeys.some((k) => can(k));

export const isSuperAdmin = () => {
  let user = JSON.parse(localStorage.getItem("user"));
  return user.roles[0].name === "Super Admin";
};

/**
 * Same check for an arbitrary user record rather than the signed-in one, used by
 * Team to recognise a Super Admin *member* in the list.
 */
export const isSuperAdminUser = (user) => (user?.roles ?? []).some((r) => r.name === "Super Admin");

/**
 * Roles the policy evaluator resolves to a full `*:*` allow by NAME, without
 * reading the policy graph at all.
 *
 * Attaching policies to one of these changes nothing, so IAM screens show their
 * access rather than a policy count and hide the attach controls — a "0
 * Policies" next to a role that can do everything is simply wrong, and an
 * Attach button there promises a grant the server will ignore.
 */
export const isAllAccessRoleName = (roleName) => roleName === "Super Admin" || roleName === "Administrator";

export const isAdmin = () => {
  let user = JSON.parse(localStorage.getItem("user"));
  if (user.roles[0].name === "Super Admin" || user.roles[0].name === "Administrator") {
    return true;
  }
  return false;
};

export const isProjectManagerOver = () => {
  let user = JSON.parse(localStorage.getItem("user"));
  if (
    user.roles[0].name === "Super Admin" ||
    user.roles[0].name === "Administrator" ||
    user.roles[0].name === "Project Manager"
  ) {
    return true;
  }
  return false;
};

export const isManagerOver = () => {
  let user = JSON.parse(localStorage.getItem("user"));
  if (
    user.roles[0].name === "Super Admin" ||
    user.roles[0].name === "Administrator" ||
    user.roles[0].name === "Project Manager" ||
    user.roles[0].name === "Manager"
  ) {
    return true;
  }
  return false;
};

export const isClericalOver = () => {
  return true;
};

// export const isClericalOver = () => {
//   let user = JSON.parse(localStorage.getItem("user"));
//   if (user.roles[0].name === "Super Admin" || user.roles[0].name === "Administrator" || user.roles[0].name === "Manager" || user.roles[0].name === "Clerical") {
//     return true;
//   }
//   return false;
// };

export const isAccountant = () => {
  let user = JSON.parse(localStorage.getItem("user"));
  return user.roles[0].name === "Accountant";
};

export const isTechnician = () => {
  let user = JSON.parse(localStorage.getItem("user"));
  return user.roles[0].name === "Technician";
};

export const isProjectManager = () => {
  let user = JSON.parse(localStorage.getItem("user"));
  return user.roles[0].name === "Project Manager";
};

export const isManager = () => {
  let user = JSON.parse(localStorage.getItem("user"));
  return user.roles[0].name === "Manager";
};

export const getMinutesFromFormattedHours = (timeString) => {
  if (!timeString) return 0;
  const [hours, minutes] = timeString.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) {
    return 0;
  }
  return hours * 60 + minutes;
};

const getPhoneCountry = () => "US";

export const formatUSPhone = (value) => {
  if (!value) return "";
  const country = getPhoneCountry();
  const parsed = country ? parsePhoneNumberFromString(value, country) : parsePhoneNumberFromString(value);
  if (parsed) return country ? parsed.formatNational() : parsed.formatInternational();
  return country ? new AsYouType(country).input(value) : new AsYouType().input(value);
};

export const isValidUSPhone = (value) => {
  if (!value) return true;
  const country = getPhoneCountry();
  return country ? isValidPhoneNumber(value, country) : isValidPhoneNumber(value);
};

export const toE164Phone = (value) => {
  if (!value) return "";
  const country = getPhoneCountry();
  const parsed = country ? parsePhoneNumberFromString(value, country) : parsePhoneNumberFromString(value);
  return parsed ? parsed.number : value;
};

export const getFormattedHoursFromMins = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = remainingMinutes.toString().padStart(2, "0");
  return `${formattedHours}:${formattedMinutes}`;
};
